import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Panel from "@ui/panel/Panel";
import Pre from "@ui/pre/Pre";
import { FileJson } from "lucide-react";
import { GD } from "src/GD";
import { FormParser } from "src/view/FormParser";
import { banRuleFormResponse, banRuleId, getBanRuleRequest } from "./banRulesData";
import type { RuleRecord } from "./ruleBlocksData";
import { saveBanRule } from "./banRuleActions";

import { AdminButton } from "./AdminControls";
import { BanRulePreview } from "./BanRulePreview";
import { readRulePreview, type RulePreview } from "./rulePreviewData";

const Content = styled.div`
	display: flex; flex: 1 1 auto; min-height: 0; min-width: 0;
	.editor-scroll { overflow: auto; min-height: 0; min-width: 486px; flex: 1 1 486px; padding: 18px; box-sizing: border-box; background: #f6f8fb; }
	.rule-preview { flex: 0 0 320px; min-width: 0; min-height: 0; }
	.state { padding: 32px; text-align: center; }
	.error { color: #b42342; }
	@media (max-width: 860px) { .editor-scroll { min-width: 0; } .rule-preview { display: none; } }
`;
export const BanRulePopup = ({ id, projectId, userId, onClose, onSaved }: { id: number; projectId: number; userId: number; onClose: () => void; onSaved: (record?: RuleRecord) => void }) => {
	useLocale();
	const { t } = useTranslation();
	const label = (key: string) => t(`banRules.editor.${key}`);
	const [preview, setPreview] = useState<RulePreview | null>(null);
	const draftRef = useRef<Record<string, unknown> | null>(null);
	const [load, setLoad] = useState<{ busy: boolean; error?: string }>({ busy: true });
	const [attempt, setAttempt] = useState(0);
	const [saving, setSaving] = useState(false);
	const [rawOpen, setRawOpen] = useState(false);
	const rawPopup = useRef<HTMLDivElement>(null);
	const mutation = useRef<AbortController | null>(null);
	const alive = useRef(true);
	const popup = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!rawOpen) return;
		const previousFocus = document.activeElement;
		rawPopup.current?.focus({ preventScroll: true });
		return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true }); };
	}, [rawOpen]);
	useEffect(() => {
		alive.current = true;
		const previousFocus = document.activeElement;
		popup.current?.focus({ preventScroll: true });
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { alive.current = false; mutation.current?.abort(); });
		return () => {
			alive.current = false; mutation.current?.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout);
			if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus({ preventScroll: true });
		};
	}, []);
	useEffect(() => {
		const pending = new AbortController();
		setLoad({ busy: true });
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => pending.abort());
		void (async () => {
			try {
				const response = banRuleFormResponse(await GD.REQ_API.request({ ...getBanRuleRequest(projectId, id), signal: pending.signal }));
				if (!alive.current || pending.signal.aborted) return;
				if (response.error) { setLoad({ busy: false, error: response.error.message }); return; }
				const next = response.data;
				const decorator = next.$__decorator as { blocks?: { gridTemplateColumns?: string }[] } | undefined;
				if (decorator?.blocks?.[0]?.gridTemplateColumns) decorator.blocks[0].gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
				draftRef.current = next; setPreview(readRulePreview(next)); setLoad({ busy: false });
			} catch { if (alive.current && !pending.signal.aborted) setLoad({ busy: false, error: "" }); }
		})();
		return () => { pending.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, [id, projectId, attempt]);
	const close = () => { if (!mutation.current) onClose(); };
	const submit = async (form: Record<string, unknown>) => {
		if (mutation.current || !alive.current) throw new Error(label("saving"));
		const data = form;
		const pending = new AbortController(); mutation.current = pending; setSaving(true);
		try {
			const response = await saveBanRule(data, userId, projectId, pending.signal);
			if (!alive.current || pending.signal.aborted) throw new Error(label("saveError"));
			const body = response.data;
			const error = response.error || body?.error;
			if (error) return { error };
			const result = body && typeof body === "object" && "data" in body ? body.data : body;
			if (result === null || result === undefined || result === false) throw new Error(label("saveError"));
			const savedId = result && typeof result === "object" ? banRuleId(result as RuleRecord) : typeof result === "number" ? banRuleId({ id: result }) : banRuleId(data);
			onSaved(savedId ? { ...data, ...(result && typeof result === "object" ? result : {}), id: savedId } : undefined); return { data: form };
		} catch (error) {
			const message = error instanceof Error && error.message === "contextChanged" ? t("bans.errors.contextChanged") : label("saveError");
			throw new Error(message);
		} finally { mutation.current = null; if (alive.current) setSaving(false); }
	};
	const title = id <= 0 ? t("banRules.create") : label("edit");
	return createPortal(<div ref={popup} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} onKeyDown={event => {
		if (event.key === "Escape") { event.stopPropagation(); if (rawOpen) setRawOpen(false); else close(); }
		if (event.key !== "Tab") return;
		const activePopup = rawOpen ? rawPopup.current : popup.current;
		const controls = activePopup?.querySelectorAll<HTMLElement>('button:not([disabled]), summary, [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]');
		const visible = Array.from(controls ?? []).filter(element => element.getClientRects().length > 0);
		if (!visible.length) { event.preventDefault(); return; }
		const first = visible[0], last = visible[visible.length - 1];
		if (event.shiftKey && (document.activeElement === first || document.activeElement === activePopup)) { event.preventDefault(); last.focus(); }
		else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
	}}>
		<Panel decorator="absolute" label={title} headerActions={<AdminButton variant="secondarySmall" size="small" disabled={load.busy || load.error !== undefined || !preview} onClick={() => setRawOpen(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><FileJson size={14} aria-hidden="true" />RAW</AdminButton>} onClose={close} growContent disableScroll contentPadding="0" sx={{ width: "1100px", maxWidth: "96vw", height: "92dvh", maxHeight: "92dvh", overflow: "hidden", backgroundColor: "panelBackgroundPrimary", "@media (max-width: 860px)": { width: "740px" } }}>
			<Content>
				<div className="editor-scroll">
					{load.busy ? <div className="state" role="status">{t("common.loading")}</div> : load.error !== undefined ? <div className="state error" role="alert"><p>{load.error || t("banRules.loadError")}</p><AdminButton variant="secondarySmall" onClick={() => setAttempt(value => value + 1)}>{t("common.refresh")}</AdminButton></div> : preview && <>
						<fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }} aria-busy={saving}>
							<FormParser<Record<string, unknown>> grow size="small"
								primaryButton={{ title: saving ? label("saving") : label("save") }} secondaryButton={{ title: t("bans.cancel"), onClick: close }}
								onRequest={async () => ({ data: draftRef.current })}
								onChange={form => { draftRef.current = form; setPreview(readRulePreview(form)); }}
								onSubmit={submit} />
						</fieldset>
					</>}
				</div>
				{!load.busy && load.error === undefined && preview && <div className="rule-preview"><BanRulePreview rule={preview} /></div>}
			</Content>
		</Panel>
		{rawOpen && <div ref={rawPopup} role="dialog" aria-modal="true" aria-label={msg("templateRaw_8b61ed51", { v0: title })} tabIndex={-1}>
			<Panel decorator="absolute" label={msg("templateRaw_8b61ed51", { v0: title })} onClose={() => setRawOpen(false)} growContent disableScroll sx={{ width: "800px", maxWidth: "94vw", height: "85dvh", maxHeight: "85dvh" }}>
				<Pre data={JSON.stringify(draftRef.current, (key, value) => key.startsWith("$__") || key.startsWith("_hint") ? undefined : value, 2)} wrap style={{ flex: "1 1 auto", minHeight: 0 }} />
			</Panel>
		</div>}
	</div>, document.body);
};
